import {
  Component,
  HostListener,
  inject,
  input,
  Renderer2,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';

import { isNil } from 'src/utils/utils';

export const MODULES = [MatButtonModule];
export const COMPONENTS = [FaIconComponent];

@Component({
  selector: 'app-print-button',
  template: `
    <ng-template #printButton>
      <button class="settings-sidenav__btn" mat-mini-fab (click)="print()">
        <fa-icon [icon]="['far', 'print']" size="lg"></fa-icon>
      </button>
    </ng-template>
  `,
  imports: [...MODULES, ...COMPONENTS],
})
export class PrintButtonComponent {
  private renderer = inject(Renderer2);
  /*EXPLANATION PROPERTIES:
    1) componentName is there to be able to prepare the layout of different components
    2) keep track of the changed elements and their changed style (needed to undo changes after print)
    3) stores the original styles of elements {element: {style: originalValue, style2: originalValue}} so that it can be reset
   */
  printButtonTemplate = viewChild<TemplateRef<any>>('printButton');
  componentName = input<string>(null); // 1) null = default if no componentName is given
  elementsThatGotHiddenForPrint: string[] = []; // 2)
  elementsWhichStyleGotChanged = []; // 3)

  print(): void {
    /* just opens the print window*/
    window.print();
  }

  /*================== functions that react to print events ================*/
  @HostListener('window:beforeprint', ['$event'])
  onBeforePrint(): void {
    /*EXPLANATION:
      onBeforePrint gets fired before window.print (print window) opens;
      is needed to prepare print layout for more complex components*/
    const componentName = this.componentName();
    if (isNil(componentName)) {
      return;
    } // gate clause --> gets immediately out of function when componentName has its default null

    switch (componentName) {
      case 'route-fcst': {
        this.prepareRouteFcstForPrint();
        this.elementsThatGotHiddenForPrint = this.hideElements([
          '.mapboxgl-ctrl-top-right',
          '.mapboxgl-ctrl-bottom-left',
          '.mapboxgl-ctrl-bottom-right',
          'app-route-fcst-legend',
        ]);
        break;
      }
    }
  }

  @HostListener('window:afterprint', ['$event'])
  onAfterPrint(event): void {
    const componentName = this.componentName();
    if (isNil(componentName)) {
      return;
    } // gate clause --> gets immediately out of function when componentName has its default null

    this.showHiddenElementsAgain(this.elementsThatGotHiddenForPrint);

    switch (componentName) {
      case 'route-fcst': {
        this.resetStylesRouteFcst();
        break;
      }
    }
  }

  /*========================== general functions ===========================*/

  setStyleForElement(
    cssSelectorForElement: string,
    styleOptions: object
  ): void {
    /*EXPLANATION:
      takes cssClass and applies all styleOptions to it
      also all style changes of that cssClass get documented in changedStylesOfElement which at the end gets pushed to
      elementsWhichStyleGotChanged --> reason: to be able to set back styles when printing is done
      EXAMPLE DATA:
      styleOptions --> {width: '100%', height: 'auto'}
      changedStylesOfElement --> {.mapboxgl-canvas:{'width': '100%'}}
      */
    const changedStylesOfElement = {};
    changedStylesOfElement[cssSelectorForElement] = {};

    const elementStyleShouldBeApplied = this.getFirstElementFromDOM(
      cssSelectorForElement
    );

    if (elementStyleShouldBeApplied !== null) {
      for (const [key, value] of Object.entries(styleOptions)) {
        changedStylesOfElement[cssSelectorForElement][`${key}`] = value;
        this.renderer.setStyle(elementStyleShouldBeApplied, `${key}`, value);
      }
    }
    this.elementsWhichStyleGotChanged.push(changedStylesOfElement);
  }

  resetStyles(changedStylesOfElements: object[]): void {
    // const cssSelektorsOfChangedElements = []; // e.g [".mapboxgl-canvas", ".radar-info-card"]
    // const stylesThatGotChanged = [];
    /*get the cssSelectors which are the keys of changedStylesOfElements object*/
    /*for (const element of changedStylesOfElements){
      const singleCssSelector = Object.keys(element);
      cssSelektorsOfChangedElements.push(singleCssSelector[0]);
    }*/
  }

  hideElements(cssSelectorForElement: string[]): string[] {
    /* EXPLANATION:
       takes list of cssSelectors of the elements that should get hidden and returns a list with hidden elements
       --> reason: to be able to show elements again after printing
       */
    const elementsThatGotHidden: string[] = [];

    for (const element of cssSelectorForElement) {
      const elementStyleShouldBeApplied = this.getFirstElementFromDOM(element);
      this.renderer.setStyle(elementStyleShouldBeApplied, 'display', 'none');
      elementsThatGotHidden.push(element);
    }
    return elementsThatGotHidden;
  }

  showHiddenElementsAgain(cssSelectorForElement: string[]): void {
    for (const element of cssSelectorForElement) {
      const elementStyleShouldBeApplied = this.getFirstElementFromDOM(element);
      this.renderer.setStyle(elementStyleShouldBeApplied, 'display', 'unset');
    }
  }

  getFirstElementFromDOM(cssSelector: string): object {
    /*gets you the first element in DOM that matches the selector*/
    return document.querySelector(cssSelector);
  }

  getAllElementsFromDOM(cssSelector: string): object {
    /*gets you all elements in DOM that matches the selector*/
    return document.querySelectorAll(cssSelector);
  }

  /*========================= functions for route-fcst =================*/
  prepareRouteFcstForPrint(): void {
    this.setStyleForElement('.mapboxgl-canvas', {
      width: '100%',
      height: 'auto',
    });
  }

  resetStylesRouteFcst(): void {
    /*IDEA:
      iterate over 'elementsWhichStyleGotChanged'; take each cssClass and apply all 'original' styles back to it
    */
  }

  /*===================== helper functions ==========================*/

  /*can be deleted if everything works*/
  prepareMapForPrint(): void {
    // klappt so krieg ich element
    /*const mapHtmlElement = document.querySelector('.mapboxgl-canvas');
    this.renderer.setStyle(mapHtmlElement, 'width', '300px');*/
    /*const test = mapComponent.map.getCanvas().toDataURL();*/

    /*funzt*/
    /*const ratioElement = document.querySelector('.ratio');*/

    /*const heightOfRatioElement = ratioElement.clientHeight;
    const widthOfRatioElement = ratioElement.clientWidth;*/

    const mapBoxCanvasElement = document.querySelector('.mapboxgl-canvas');
    this.renderer.setStyle(mapBoxCanvasElement, 'width', '100%');
    this.renderer.setStyle(mapBoxCanvasElement, 'height', 'auto');

    /*ausblenden control right*/
    const mapControlRightTop = document.querySelector(
      '.mapboxgl-ctrl-top-right'
    );
    this.renderer.setStyle(mapControlRightTop, 'display', 'none');

    /*ausblenden select-layer-button*/
    const mapboxglCtrlTopLeft = document.querySelector(
      '.mgl-map__select-layer-button'
    );
    this.renderer.setStyle(mapboxglCtrlTopLeft, 'display', 'none');

    /*set radar-info-card to full din a 4 width*/
    const radarInfoCard = document.querySelector('.radar-info-card');
    this.renderer.setStyle(radarInfoCard, 'max-width', '100%');

    /*window.print();*/
  }
}
